import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_serverEndpoint;

/**
 * Analysiert eine hochgeladene Lenex oder DSV Datei
 * @param {File} file - Die hochzuladende Datei
 * @param {string} token - JWT Access Token
 * @returns {Promise} - Response mit fileId und Analyse-Ergebnissen
 */
export const analyzeCompetitionFile = async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${API_BASE_URL}/Competition/AnalyzeFile`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    });

    return response.data;
};

/**
 * Importiert Lenex-Daten mit den angegebenen Einstellungen
 * @param {Object} settings - Import-Einstellungen
 * @param {File|null} file - Optional: Datei (wenn nicht bereits analysiert)
 * @param {string} token - JWT Access Token
 * @returns {Promise} - Response mit Import-Ergebnissen
 */
export const importLenex = async (settings, file, token) => {
    const formData = new FormData();
    formData.append('settingsJson', JSON.stringify(settings));
    
    if (file) {
        formData.append('file', file);
    }

    const response = await axios.post(`${API_BASE_URL}/Competition/ImportLenex`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    });

    return response.data;
};

/**
 * Importiert DSV-Daten mit den angegebenen Einstellungen
 * @param {Object} settings - Import-Einstellungen
 * @param {File|null} file - Optional: Datei (wenn nicht bereits analysiert)
 * @param {string} token - JWT Access Token
 * @returns {Promise} - Response mit Import-Ergebnissen
 */
export const importDSV = async (settings, file, token) => {
    const formData = new FormData();
    formData.append('settingsJson', JSON.stringify(settings));
    
    if (file) {
        formData.append('file', file);
    }

    const response = await axios.post(`${API_BASE_URL}/Competition/ImportDSV`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    });

    return response.data;
};

/**
 * Analysiert eine CSV Datei fuer Kampfrichter (serverseitig)
 * @param {File} file - CSV Datei
 * @param {string} competitionId - Zielwettkampf
 * @param {string} token - JWT Access Token
 */
export const analyzeOfficialsCsv = async (file, competitionId, token) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('competitionId', competitionId);

    const response = await axios.post(`${API_BASE_URL}/Competition/AnalyzeOfficialsCsv`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    });

    return response.data;
};

/**
 * Importiert Kampfrichter aus einer CSV Datei (serverseitig)
 * @param {Object} settings - Import Einstellungen (competitionId, replaceExisting, optional fileId)
 * @param {File|null} file - CSV Datei wenn kein fileId vorhanden
 * @param {string} token - JWT Access Token
 */
export const importOfficialsCsv = async (settings, file, token) => {
    const formData = new FormData();
    formData.append('settingsJson', JSON.stringify(settings));
    if (file) {
        formData.append('file', file);
    }

    const response = await axios.post(`${API_BASE_URL}/Competition/ImportOfficialsCsv`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    });

    return response.data;
};

/**
 * Exportiert Wettkampfdaten im Lenex-Format
 * @param {Object} settings - Export-Einstellungen
 * @param {string} token - JWT Access Token
 * @returns {Promise<Blob>} - Lenex XML Datei als Blob
 */
export const exportLenex = async (settings, token) => {
    const formData = new FormData();
    formData.append('settingsJson', JSON.stringify(settings));

    const response = await axios.post(`${API_BASE_URL}/Competition/ExportLenex`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        },
        responseType: 'blob'
    });

    return response.data;
};

/**
 * Exportiert Wettkampfdaten im DSV-Format
 * @param {Object} settings - Export-Einstellungen (inkl. exportType)
 * @param {string} token - JWT Access Token
 * @returns {Promise<Blob>} - DSV Text Datei als Blob
 */
export const exportDSV = async (settings, token) => {
    const formData = new FormData();
    formData.append('settingsJson', JSON.stringify(settings));

    const response = await axios.post(`${API_BASE_URL}/Competition/ExportDSV`, formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        },
        responseType: 'blob'
    });

    return response.data;
};

/**
 * Hilfsfunktion zum Download einer Blob-Datei im Browser
 * @param {Blob} blob - Die herunterzuladende Datei als Blob
 * @param {string} filename - Der Dateiname für den Download
 */
export const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};
